/**
 * One terrain profile per plan, shared by the two places that show it: the map's
 * route panel (per leg — the sparkline, the highest ground, the tallest
 * obstacle) and the Formular's route section (the Sicherheitshöhe column it
 * fills).
 *
 * It wraps the whole editor — map AND form — for the same reason
 * {@link WindsProvider} does: the numbers are read in one half and printed in the
 * other. It fetches by itself, debounced after every route change, exactly like
 * the airspace check: what the ground looks like under a drawn route is a fact
 * the pilot should not have to ask for — and, since this pass, so is the
 * Sicherheitshöhe suggestion that follows from it (`terrain.ts`).
 *
 * The shared read-only view has no provider at all: `useTerrain()` is null there
 * and the form falls back to what the plan REMEMBERS (`plans.meta.terrain`).
 */
import {
  createContext, useCallback, useContext, useMemo, useState, type ReactNode,
} from 'react';

import { type TerrainProfile } from './api';
import { usePublishFreshness } from './freshness';
import { usePlanRev, usePlanStore } from './planContext';
import { useAutoLoad } from './useAutoLoad';
import {
  DEFAULT_CORRIDOR_NM, loadTerrain, storedRows, terrainRows, terrainSignature,
  type TerrainRow,
} from './terrain';

const LS_CORRIDOR = 'avia.map.corridor';
/** One debounce for a whole gesture — a dragged waypoint is a dozen commits. */
const DEBOUNCE_MS = 800;

const readCorridor = (): number => {
  try {
    const n = Number(localStorage.getItem(LS_CORRIDOR));
    if (Number.isFinite(n) && n >= 1 && n <= 10) return n;
  } catch {
    /* private mode */
  }
  return DEFAULT_CORRIDOR_NM;
};

export interface TerrainView {
  /** one row per leg — from the live profile, or from what the plan remembers */
  rows: TerrainRow[];
  /** what the endpoint last answered; null before the first profile arrives */
  profile: TerrainProfile | null;
  busy: boolean;
  error: unknown;
  /** the corridor the profile is read over, NM */
  corridor: number;
  setCorridor(nm: number): void;
  /** how many legs the route currently has */
  count: number;
  /** legs whose Sicherheitshöhe the pilot owns — the suggestion was not written there */
  kept: number[];
  /** read it again now — the section header's ⟳ */
  refresh(): void;
}

const TerrainContext = createContext<TerrainView | null>(null);

export function TerrainProvider({ children }: { children: ReactNode }) {
  const store = usePlanStore();
  const rev = usePlanRev();
  const [corridor, setCorridorState] = useState<number>(readCorridor);
  const [profile, setProfile] = useState<TerrainProfile | null>(null);
  const [kept, setKept] = useState<number[]>([]);

  // `store` is a stable singleton whose identity never moves — `rev` is what changes
  const waypoints = useMemo(() => store.route?.waypoints ?? [], [store, rev]);
  /** the value identity of everything a profile depends on; a keystroke elsewhere costs nothing */
  const signature = useMemo(
    () => (waypoints.length < 2 ? '' : terrainSignature(waypoints, corridor)),
    [waypoints, corridor],
  );

  const setCorridor = useCallback((nm: number) => {
    setCorridorState(nm);
    try {
      localStorage.setItem(LS_CORRIDOR, String(nm));
    } catch {
      /* the choice simply does not survive the tab */
    }
  }, []);

  const auto = useAutoLoad({
    signature,
    debounceMs: DEBOUNCE_MS,
    run: async ({ signal }) => {
      const res = await loadTerrain(store, { corridorNm: corridor, signal });
      // null = the route moved on while the profile was out; the next pass is already queued
      if (!res) return;
      setProfile(res.profile);
      setKept(res.kept);
    },
  });

  // a route with fewer than two waypoints has no profile to show, and the last
  // one drawn must not go on describing ground the plan no longer crosses
  const shown = signature === '' ? null : profile;

  usePublishFreshness(
    'terrain',
    useMemo(
      () => ({ busy: auto.busy, error: auto.error, at: auto.at, refresh: auto.refresh }),
      [auto.busy, auto.error, auto.at, auto.refresh],
    ),
  );

  /**
   * The live profile when there is one, and what the plan remembers until then.
   * The stored summary is what a plan opened offline (or on a server with no DEM
   * archive) keeps showing — the same two numbers, without the drawing.
   */
  const rows = useMemo(
    () => (shown ? terrainRows(shown.legs, waypoints) : storedRows(store.terrainStatus(), waypoints)),
    [shown, waypoints, store, rev],
  );

  const value = useMemo<TerrainView>(
    () => ({
      rows,
      profile: shown,
      busy: auto.busy,
      error: auto.error,
      corridor,
      setCorridor,
      count: Math.max(0, waypoints.length - 1),
      kept,
      refresh: auto.refresh,
    }),
    [rows, shown, auto.busy, auto.error, auto.refresh, corridor, setCorridor, waypoints.length, kept],
  );

  return <TerrainContext.Provider value={value}>{children}</TerrainContext.Provider>;
}

/** null outside a {@link TerrainProvider} — the shared read-only view never fetches. */
export function useTerrain(): TerrainView | null {
  return useContext(TerrainContext);
}
